import { memo } from "react";
import { bool, node, oneOf, oneOfType, string } from "prop-types";
import { parsePrice } from "@repo/utils";
import { MAX_VALUES } from "@utils";
import Grid from "@material-ui/core/Grid";
import { capitalize } from "@material-ui/core";
import { ValueCellReadOnlyContent } from "../components/ValueCellReadOnlyContent";
import { ValueCellEditableContent } from "../components/ValueCellEditableContent";
import useStyle from "./PriceCell.style";
import useCellStyle from "../../../../styles/CommonTableCellStyles.style";
import classnames from "classnames";
import { InputCellProps } from "../types";

export const PriceCell = memo(({
  colName,
  isSticky,
  useDynamicWidth,
  allowOverflowDisplay,
  onValueChange,
  displayContent,
  displayMax,
  editable,
  wider,
  widest,
  narrow,
  children,
  mini,
  noContainerBackground,
  highlightCellText,
  className,
  isHeader,
  valueCellWidth,
  messageContent,
  focusAction,
  usePresetClass = true,
  useSectionClass = true,
  style,
  allowNegative,
  size="large",
  alignItems,
  extendPadding,
  addInnerMargin,
  ...valueProps
}) => {
  const classes = useStyle({ colName, extendPadding });
  const cellClasses = useCellStyle();
  
  const dw = useDynamicWidth && colName;
  
  return (
    <Grid
      item
      container
      role="rowitem"
      alignItems={alignItems || (isHeader ? "flex-end" : "flex-start")}
      className={classnames(
        "cell",
        cellClasses.cell,
        !dw && classes.priceCell,
        !dw && mini && classes.cellMini,
        !dw && narrow && classes.cellSmall,
        !dw && wider && classes.cellMedium,
        !dw && widest && classes.cellLarge,
        dw && classes.dynamicWidth,
        
        cellClasses.bolder,
        isHeader && cellClasses.headCell,
        !isHeader && classes.priceCellBody,
        cellClasses[`size${capitalize(size)}`],
        extendPadding && classes.extendPadding,
        valueCellWidth && classes.valueCellWidth,
        isSticky && colName && classes.isSticky,
        allowOverflowDisplay && "display-overflow-right",
        highlightCellText && "highlightCellText",
        className
      )}
      style={style}
    >
      <Grid
        item container
        justifyContent="center"
        alignItems="center"
        wrap="nowrap"
        className={classnames(
          !isHeader && cellClasses.cellInner,
          isHeader && "relative",
        )}
      >
        {displayContent && (
          <Grid
            item
            container
            alignItems="center"
            justifyContent="center"
            wrap="nowrap"
            className={classnames(
              classes.priceContainer,
              !noContainerBackground && classes.priceContainerBackground,
              usePresetClass && (isHeader ? "preset-tableHeader" : "preset-price"),
              useSectionClass && "sectionTitle",
              addInnerMargin && "mt-6",
            )}
          >
            {
              (editable || messageContent || focusAction)
                ? <ValueCellEditableContent
                  {...valueProps}
                  messageContent={messageContent}
                  displayMax={displayMax}
                  onValueChange={onValueChange}
                  allowNegative={allowNegative}
                  max={MAX_VALUES.PRICE}
                  min={allowNegative ? -MAX_VALUES.PRICE : 0}
                  isSmall
                  fullWidth
                  applySuffixSpacing
                />
                : <ValueCellReadOnlyContent
                  {...valueProps}
                  displayMax={displayMax}
                  allowNegative={allowNegative}
                  parser={parsePrice}
                  applySuffixSpacing
                />
            }
          </Grid>
        )}
        {children}
      </Grid>
    </Grid>
  );
});

PriceCell.propTypes = {
  ...InputCellProps,
  colName: string,
  widest: bool,
  noContainerBackground: bool,
  usePresetClass: bool,
  valueCellWidth: bool,
  children: oneOfType([node, string]),
  // common
  emptyInputs: bool,
  highlightCellText: bool,
  showDecimals: bool,
  className: string,
  isHeader: bool,
  extendPadding: bool,
  addInnerMargin: bool,
  size: oneOf(["xSmall", "small", "medium", "large", "xLarge"]),
  alignItems: oneOf(["center", "flex-start", "flex-end"]),
  inputClassName: string,
  americanFormat: bool,
  isSticky: bool,
  useDynamicWidth: bool,
  allowOverflowDisplay: bool,
};
